import { useSession } from '../auth/session';
import type { AdminSession } from './types';

/** An error the API returned, with its Arabic sentence ready to show. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly fieldErrors: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Params = Record<string, string | number | boolean | null | undefined>;

const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? '';
const ADMIN = `${BASE}/api/v1/admin`;

let refreshing: Promise<boolean> | null = null;

/**
 * The CRM's only door to the API.
 *
 * Admin access tokens live thirty minutes. On a 401 the refresh token is spent once — concurrent
 * requests wait on the same refresh — and the request is replayed once; if that fails too, the
 * session ends and the app shows the sign-in screen.
 */
export async function api<T>(method: string, path: string, body?: unknown, params?: Params): Promise<T> {
  const response = await send(method, path, body, params);
  if (response.status === 401 && useSession.getState().refreshToken && !path.startsWith('/auth/')) {
    if (await refresh()) {
      return parse<T>(await send(method, path, body, params));
    }
    useSession.getState().signOut('انتهت الجلسة، سجّل الدخول من جديد');
  }
  return parse<T>(response);
}

export const get = <T>(path: string, params?: Params) => api<T>('GET', path, undefined, params);
export const post = <T>(path: string, body?: unknown) => api<T>('POST', path, body ?? {});
export const put = <T>(path: string, body: unknown) => api<T>('PUT', path, body);
export const patch = <T>(path: string, body: unknown) => api<T>('PATCH', path, body);
export const del = (path: string) => api<void>('DELETE', path);

function url(path: string, params?: Params): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, String(value));
    }
  }
  const qs = query.toString();
  return `${ADMIN}${path}${qs ? `?${qs}` : ''}`;
}

async function send(method: string, path: string, body?: unknown, params?: Params): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  const token = useSession.getState().accessToken;
  if (token && !path.startsWith('/auth/login') && !path.startsWith('/auth/code')) {
    headers.Authorization = `Bearer ${token}`;
  }
  try {
    return await fetch(url(path, params), {
      method,
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new ApiError('تعذّر الاتصال بالخادم، تحقق من الاتصال', 0, 'NETWORK');
  }
}

async function parse<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  const data: unknown = text ? JSON.parse(text) : undefined;
  if (!response.ok) {
    const problem = (data ?? {}) as { detail?: string; code?: string; errors?: Record<string, string> };
    if (problem.code === 'ADMIN_PASSWORD_CHANGE_REQUIRED') {
      useSession.getState().requirePasswordChange();
    }
    throw new ApiError(problem.detail ?? 'حدث خطأ غير متوقع', response.status, problem.code ?? 'UNKNOWN',
      problem.errors ?? {});
  }
  return data as T;
}

function refresh(): Promise<boolean> {
  refreshing ??= (async () => {
    const refreshToken = useSession.getState().refreshToken;
    if (!refreshToken) {
      return false;
    }
    try {
      const response = await fetch(url('/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        return false;
      }
      useSession.getState().signIn((await response.json()) as AdminSession);
      return true;
    } catch {
      return false;
    }
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

/** The message to show for anything a mutation threw. */
export function errorMessage(cause: unknown): string {
  return cause instanceof ApiError ? cause.message : 'حدث خطأ غير متوقع';
}
